import { cognitoUserAttributes } from '@root/helpers/auth';
import { Amplify } from 'aws-amplify';
import { fetchAuthSession, fetchUserAttributes, getCurrentUser, signOut } from 'aws-amplify/auth';
import type { AxiosRequestConfig } from 'axios';
import { get } from 'lodash-es';

import type { BaseUser, IAuthProvider } from './types';

export class CognitoAuthProvider implements IAuthProvider {
  constructor() {
    Amplify.configure({
      Auth: {
        Cognito: {
          // region: 'eu-west-1',
          userPoolId: import.meta.env?.VITE_COGNITO_USER_POOL_ID,
          userPoolClientId: import.meta.env?.VITE_COGNITO_WEB_CLIENT_ID,
          loginWith: {
            oauth: {
              domain: import.meta.env?.VITE_AUTH_DOMAIN,
              scopes: ['email', 'openid', 'profile', 'aws.cognito.signin.user.admin'],
              redirectSignIn: [import.meta.env?.VITE_COGNITO_REDIRECT],
              redirectSignOut: [import.meta.env?.VITE_COGNITO_REDIRECT_SIGN_OUT],
              responseType: 'code',
            },
          },
        },
      },
    });
  }

  async attemptGetUserInfo(): Promise<BaseUser | null> {
    try {
      const amplifyUser = await getCurrentUser();
      const userAttributes = await fetchUserAttributes();
      const attributes = cognitoUserAttributes(userAttributes);

      return {
        email: attributes.email,
        firstName: attributes.given_name,
        lastName: attributes.family_name,
        role: attributes['custom:role'],
        sub: attributes.sub,
        tenantId: attributes['custom:tenant_id'],
        tenantIds: JSON.parse(get(attributes, 'custom:teams', '[]')),
        username: amplifyUser.username,
      } as BaseUser;
    } catch {
      return null;
    }
  }

  async getNetworkAuthPayload(): Promise<AxiosRequestConfig | null> {
    try {
      const session = await fetchAuthSession();
      const accessToken = session.tokens?.idToken?.toString();

      if (!accessToken) {
        return null;
      }

      return { headers: { Authorization: accessToken } };
    } catch {
      return null;
    }
  }

  async logout() {
    await signOut();
  }

  async getFreshUserInfo() {
    try {
      // In Amplify v6, fetchAuthSession with forceRefresh refreshes tokens automatically
      await fetchAuthSession({ forceRefresh: true });
      return await this.attemptGetUserInfo();
    } catch (_error) {
      await signOut();
      return null;
    }
  }

  getAuthSearchParams() {
    const searchParams = new URLSearchParams();
    return searchParams.toString();
  }
}
